import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { CUSTOMER_TYPE, CustomerType } from "../../../../constants";
import { Form, RootState } from "../../../../models";
import {
    Address,
    EmployeeInfoFields,
    EmployeeInfoState,
    OnboardingState,
    SubscriptionDetailsState,
} from "../../../../models/state";
import * as CUSTOMER_INFO_SELECTORS from "../../checkout/state/selectors/customerInfoSelectors";

export const getOnboardingData = (state: RootState): OnboardingState => _get(state, "onboarding");

export const getEmailFormById = (state: RootState, id: "onboardingEmailForm"): Form => getOnboardingData(state)[id];

export const getPersonalDetails = (state: RootState): Partial<EmployeeInfoState> =>
    getOnboardingData(state).personalDetails || {};

export const getEmployeeInfo = (state: RootState): EmployeeInfoFields | undefined => {
    const personalDetails = getPersonalDetails(state);
    return personalDetails?.employeeInfo || undefined;
};

export const isAllowedToProceed = (state: RootState): boolean =>
    getPersonalDetails(state).allowedToProceed ||
    (CUSTOMER_INFO_SELECTORS.getCurrentType(state) === CUSTOMER_TYPE.NEW &&
        !!getSubscriptionDetails(state).check.isAllowedToProceed);

export const isFormerLegalOwnerSameAsBankId = (state: RootState): boolean =>
    CUSTOMER_INFO_SELECTORS.getCurrentType(state) === CUSTOMER_TYPE.EXISTING &&
    !!getSubscriptionDetails(state).check.isFormerLegalOwnerSameAsBankId;

export const getSubscriptionDetails = (state: RootState): SubscriptionDetailsState =>
    getOnboardingData(state).subscriptionDetails;

export const getRequestedPortinDate = (state: RootState): string | undefined =>
    getSubscriptionDetails(state).requestedPortinDate || undefined;

export const getAllowedExtraSim = (state: RootState): string[] | undefined =>
    getSubscriptionDetails(state).allowedExtraSim || undefined;

export const getEmployeeFullName = (state: RootState): string | undefined => {
    const personalDetails = getPersonalDetails(state).employeeInfo;
    return personalDetails?.firstName + " " + personalDetails?.lastName || "";
};

export const isEsimSelected = (state: RootState): boolean => getSubscriptionDetails(state).isEsimSelected || false;

export const isEmailValid = (state: RootState): boolean => {
    const emailError: boolean | undefined =
        getOnboardingData(state).onboardingEmailForm?.errors?.details?.productUserEmail;
    const emailValue = getOnboardingData(state).onboardingEmailForm?.values?.details?.productUserEmail;
    return !emailError && !!emailValue;
};

export const getDeliveryAddress = (state: RootState): Address | undefined => {
    return getEmployeeInfo(state)?.address;
};

export const getEmailAddress = (state: RootState): string | undefined => {
    return getEmployeeInfo(state)?.email;
};

export const isSubmitEmailSuccess = (state: RootState): boolean => {
    return !!getPersonalDetails(state)?.submitEmailSuccess;
};

export const isOrderFound = (state: RootState): boolean => {
    return getOnboardingData(state)?.isOrderFound || false;
};

export const isOrderSubmitable = (state: RootState): boolean => {
    return getOnboardingData(state)?.isOrderSubmitable || false;
};

export const isSubmitSubscriptionDetailsSuccess = (state: RootState): boolean => {
    return !!getPersonalDetails(state).submitSubscriptionDetailsSuccess;
};

export const isPortinSelected = (state: RootState): boolean =>
    isAllowedToProceed(state) && getSubscriptionDetails(state).check.type === CUSTOMER_TYPE.PORT_IN;

export const isSubmitSuccess = (state: RootState): boolean => getOnboardingData(state)?.submit?.success || false;

export const getPhoneNumberCheckType: (state: RootState) => CustomerType | undefined = (state) =>
    getSubscriptionDetails(state)?.check?.type || undefined;

export const isExistingPhoneNumberActive: (state: RootState) => boolean = (state) =>
    getSubscriptionDetails(state)?.check?.isPhoneNumberActive || false;

export const getAddedServices = (state: RootState): string[] | undefined => {
    const addedServices = getSubscriptionDetails(state).addedServices;
    if (_isEmpty(addedServices)) {
        return undefined;
    }
    return addedServices;
};

export const getLegalOwnerOrgName = (state: RootState): string | undefined =>
    getOnboardingData(state)?.newLegalOwner?.orgName || undefined;
